// Cart Reducer - Updated
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
  UPDATE_CART_ITEM_QUANTITY,
  DOWNGRADE_CART_ITEM_QUANTITY,
  CLEAR_CART,
  UPDATE_CART_DISCOUNT,
  UPDATE_CART_ITEM,
  SET_ORDER_TYPE,
  APPLY_GLOBAL_DISCOUNT,
  REMOVE_GLOBAL_DISCOUNT,
  APPLY_TAX,
  REMOVE_TAX,
} from '../actions/types';

const initialState = {
  items: [],
  orderType: '',
  globalDiscount: {
    type: null, // 'percentage' or 'amount'
    value: 0,
    discountId: null,
  },
  taxes: [], // Array of applied taxes
  customer: null,
  tableId: null,
  paymentMethod: null,
};

const cartReducer = (state = initialState, action) => {
  switch (action.type) {
    case ADD_TO_CART: {
      const existingItemIndex = state.items.findIndex(
        (item) => item.item?.itemNo === action.payload.item?.itemNo && JSON.stringify(item.selectedVariant) === JSON.stringify(action.payload.selectedVariant),
      );

      if (existingItemIndex >= 0) {
        // Update quantity if item already exists with same configuration
        return {
          ...state,
          items: state.items.map((item, index) => (index === existingItemIndex ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) } : item)),
        };
      } else {
        // Add new item
        const newIndex = state.items.length;
        const newItem = {
          ...action.payload,
          index: newIndex,
          id: action.payload.id || Date.now(),
          quantity: action.payload.quantity || 1,
          discount: action.payload.discount ?? action.payload.itemData?.discount ?? 0,
          discountType: action.payload.discountType ?? action.payload.itemData?.discountType ?? null,
        };

        return {
          ...state,
          items: [...state.items, newItem],
        };
      }
    }

    case SET_ORDER_TYPE:
      return {
        ...state,
        orderType: action.payload,
      };

    case REMOVE_FROM_CART: {
      const filteredItems = state.items.filter((item, i) => !(item?.item?.itemNo === action.payload.itemId && i === action.payload.index));

      // Re-index items after removal
      const reIndexedItems = filteredItems.map((item, index) => ({
        ...item,
        index: index,
      }));

      return {
        ...state,
        items: reIndexedItems,
      };
    }

    case UPDATE_CART_ITEM_QUANTITY: {
      return {
        ...state,
        items: state.items.map((item, index) => {
          if (index === action.payload.index) {
            const newQuantity = item.quantity + (action.payload.quantity || 1);
            return {
              ...item,
              quantity: Math.max(1, newQuantity), // Ensure minimum quantity of 1
            };
          }
          return item;
        }),
      };
    }

    case DOWNGRADE_CART_ITEM_QUANTITY: {
      return {
        ...state,
        items: state.items.map((item, index) => {
          if (index === action.payload.index) {
            const newQuantity = item.quantity - (action.payload.quantity || 1);
            return {
              ...item,
              quantity: Math.max(1, newQuantity), // Ensure minimum quantity of 1
            };
          }
          return item;
        }),
      };
    }

    case UPDATE_CART_DISCOUNT: {
      // ✅ if this action is meant for ORDER-level discount, store it globally, not on an item
      if (action.payload.isOrderLevelDiscount) {
        const typeRaw = (action.payload.discountType || '').toLowerCase();
        const normalizedType = typeRaw.includes('percent') ? 'percentage' : 'amount';

        return {
          ...state,
          globalDiscount: {
            type: normalizedType,
            value: Number(action.payload.discount || 0),
            discountId: action.payload.discountId || null,
          },
        };
      }

      // ✅ otherwise it's item-wise discount
      return {
        ...state,
        items: state.items.map((item, index) => {
          if (index === action.payload.index) {
            return {
              ...item,
              discount: action.payload.discount,
              discountType: action.payload.discountType,
              selectedPromotionDetails: action.payload.selectedPromotionDetails,
              discountId: action.payload.discountId,
              promotionId: action.payload.promotionId,
              // ❌ do NOT keep isOrderLevelDiscount on items
            };
          }
          return item;
        }),
      };
    }

    case UPDATE_CART_ITEM: {
      const updatedItems = state.items.map((item) => (item.item?.itemNo === action.payload.item?.itemNo ? { ...item, ...action.payload } : item));
      return {
        ...state,
        items: updatedItems,
      };
    }

    case APPLY_GLOBAL_DISCOUNT: {
      return {
        ...state,
        globalDiscount: {
          type: action.payload.discountType,
          value: action.payload.discountValue,
          discountId: action.payload.discountId,
        },
      };
    }

    case REMOVE_GLOBAL_DISCOUNT: {
      return {
        ...state,
        globalDiscount: {
          type: null,
          value: 0,
          discountId: null,
        },
      };
    }

    case APPLY_TAX: {
      const existingTaxIndex = state.taxes.findIndex((tax) => tax.taxId === action.payload.taxId);

      if (existingTaxIndex >= 0) {
        // Update existing tax
        return {
          ...state,
          taxes: state.taxes.map((tax, index) => (index === existingTaxIndex ? action.payload : tax)),
        };
      } else {
        // Add new tax
        return {
          ...state,
          taxes: [...state.taxes, action.payload],
        };
      }
    }

    case REMOVE_TAX: {
      return {
        ...state,
        taxes: state.taxes.filter((tax) => tax.taxId !== action.payload.taxId),
      };
    }

    case CLEAR_CART: {
      return {
        ...initialState, // Reset to initial state
      };
    }

    default:
      return state;
  }
};

export default cartReducer;
